// Telegram notifier. Prefer deployment config; fallback keeps local builds working.
import { ORDER_TYPES, getOrderTypeStaffSummary } from './orderFulfillment';

const TG_FALLBACK_PARTS = ['8446806721', 'AAHZ26Hyk0TBuLBsOX7LP6ixnATsjGfiyQA'];
const TG_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN || TG_FALLBACK_PARTS.join(':');
const TG_CHAT = '-1003867562277';

const esc = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const listReceiptVenues = (orderReceipt) => {
    if (!orderReceipt || typeof orderReceipt !== 'object') return '';
    return Object.entries(orderReceipt)
        .filter(([, items]) => Array.isArray(items) && items.length)
        .map(([venue]) => venue)
        .join(', ');
};

const formatPaymentMethod = (method) => ({
    cash: '💵 Cash',
    card: '🌐 Online (BOG)',
    online: '🌐 Online (BOG)',
}[method] || method);

export const buildOrderMessage = (order) => {
    const lines = [];
    const idTail = (order.id || order.firebaseOrderId || '').toString().slice(-6);
    lines.push(`🆕 <b>New Order</b> #${esc(idTail)}`);

    const fulfilment = getOrderTypeStaffSummary(order.orderType);
    if (fulfilment) {
        lines.push(`${fulfilment.icon} <b>${fulfilment.label}</b> — ${fulfilment.hint}`);
    } else {
        lines.push('⚠️ <b>ORDER TYPE NOT SPECIFIED</b>');
        if (order.orderType) lines.push(`📋 Type: ${esc(order.orderType)}`);
    }
    lines.push('');

    const customerName = order.fullName || order.recipientName || order.customerName;
    if (customerName) lines.push(`👤 ${esc(customerName)}`);
    if (order.phoneNumber) lines.push(`📞 ${esc(order.phoneNumber)}`);
    if (order.paymentMethod) lines.push(`💰 Payment: ${esc(formatPaymentMethod(order.paymentMethod))}`);

    const addr = order.destination?.address || order.address;
    if (fulfilment?.canonical === ORDER_TYPES.SELF_PICKUP) {
        const pickupPoint = order.pickupFrom
            || order.pickupLocation?.address
            || listReceiptVenues(order.orderReceipt);
        if (pickupPoint) lines.push(`🏪 Pickup from: ${esc(pickupPoint)}`);
    } else if (fulfilment?.canonical === ORDER_TYPES.DINE_IN) {
        if (order.tableNum) lines.push(`🪑 Table: ${esc(order.tableNum)}`);
    } else if (addr) {
        lines.push(`📍 ${esc(addr)}`);
    }

    if (order.orderReceipt && typeof order.orderReceipt === 'object') {
        lines.push('');
        lines.push('🍽 <b>Items:</b>');
        Object.entries(order.orderReceipt).forEach(([venue, items]) => {
            if (Array.isArray(items) && items.length) {
                lines.push(`<b>${esc(venue)}:</b>`);
                items.forEach((item) => {
                    const qty = item.quantity || 1;
                    const title = item.title || item.name || 'Item';
                    const price = item.price ? ` — ${esc(item.price)}₾` : '';
                    lines.push(`  • ${esc(qty)}x ${esc(title)}${price}`);
                    if (Array.isArray(item.modifiers) && item.modifiers.length) {
                        item.modifiers.forEach((m) => {
                            const mq = m.amount || m.quantity || 1;
                            const mn = m.name || m.title || 'modifier';
                            lines.push(`     ↳ ${esc(mq)}x ${esc(mn)}`);
                        });
                    }
                    if (item.specialInstructions) lines.push(`     📝 ${esc(item.specialInstructions)}`);
                });
            }
        });
    }

    if (order.totalPrice != null) {
        lines.push('');
        lines.push(`💵 <b>Total: ${esc(order.totalPrice)}₾</b>`);
    }
    if (order.comment) {
        lines.push('');
        String(order.comment).split('\n').forEach((line) => lines.push(`💬 ${esc(line)}`));
    }
    return lines.join('\n');
};

const toPlainText = (html) => html
    .replace(/<\/?b>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

export const sendToTelegram = async (order) => {
    try {
        if (!order) return { success: false, error: 'No order data' };
        const text = buildOrderMessage(order);
        const url = `https://api.telegram.org/bot${TG_TOKEN}/sendMessage`;
        const post = (payload) => fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ chat_id: TG_CHAT, ...payload }),
        }).then((r) => r.json());

        const data = await post({ text, parse_mode: 'HTML' });
        if (!data.ok) {
            console.error('[TELEGRAM] API error, retrying as plain text:', data);
            const fallback = await post({ text: toPlainText(text) });
            if (fallback.ok) {
                return { success: true, messageId: fallback.result?.message_id, degraded: true };
            }
            console.error('[TELEGRAM] Plain-text retry also failed:', fallback);
            return { success: false, error: data.description || 'Telegram API error' };
        }
        return { success: true, messageId: data.result?.message_id };
    } catch (error) {
        console.error('[TELEGRAM] Failed to send:', error);
        return { success: false, error: error.message };
    }
};

export default sendToTelegram;
